
import { BehaviorSubject } from 'rxjs';
import { AppState } from './core/AppState';
import { PublicModelApiInterface, WidgetError, WidgetInterface, WidgetSubject, WidgetSubscription } from './core/Widget';
/* DISABLE SENTRY (apiCient.ts as well)
import * as Sentry from "@sentry/browser";
Sentry.init({
    dsn: "https://26555f0aeb5afc843351159f8f8759c3@o4509044251361280.ingest.de.sentry.io/4510976215220304",
    sendDefaultPii: true,
    enabled: process.env.NODE_ENV === "production",
    release: process.env.APP_VERSION_HASH,
    environment: process.env.NODE_ENV,
});
*/


export type InitializeConfig = { [key: string]: any};
export type InitializedConfig = { [key: string]: any } & { game: Game };

export function getGame(ident: string = "undefined"){
    return new Game(ident);
}

class Game{
    public userToken = new BehaviorSubject<string | null>(null);
    public constructor(public ident: string){

    }

    private getInitializedConfig(config: InitializeConfig = {}): InitializedConfig {
        return {
            ...config,
            game: this
        };
    }

    /**
     * Dynamically loads the module and initializes the drawing widget.
     * @param elementId - The ID of the HTML element where the widget should be rendered.
     * @param config - The config of widget.
     * @returns A Promise that resolves to an instance of PublicModuleApi with the initialized widget.
     */
    public async initializeDrawingWidget(
        elementId: string, 
        config: InitializeConfig & { onFullscreenChange: undefined | Function } = { onFullscreenChange: undefined }
    ): Promise<PublicModelApiInterface> {
        let getModule : () => Promise<any> = async () => { return null; }
        if(this.ident == "aviator") {
            getModule = async () => {
                const module = await import(/* webpackChunkName: "aviator-module-chunk" */ './widgets/aviator/aviator.module');
                return module.AviatorModule;
            }
        }
        return await this.initializAngularWidget(
            elementId,
            config,
            getModule
        );
    }

    private async initializAngularWidget(elementId: string, config: InitializeConfig = {}, getModule: () => Promise<any>): Promise<PublicModelApiInterface> {
        const widget = await import(/* webpackChunkName: "angular-widget-chunk" */ './widgets/AngularWidget');

        return new PublicModuleApi(widget.AngulerWidget.create(elementId, this.getInitializedConfig(config), await getModule()));
    }

    /**
    * Destroys all managed widgets.
    */
    public destroyAll() {
        PublicModuleApi.destroyAll(this.ident);
    }

    /**
     * Sets the user token for authentication.
     * @param token - The user token.
     */
    public setUserToken(token: string) {
        this.userToken.next(token);
    }

    /**
     * Clears the user token.
     */
    public clearUserToken() {
        this.userToken.next(null);
    }
}

/**
 * Public API for managing widgets. Provides methods for widget lifecycle and interaction.
 */
class PublicModuleApi<T extends WidgetInterface> implements PublicModelApiInterface {
    private static initializedWidgets : { [key: string]: Array<WidgetInterface> } = {};
    private subscription = new WidgetSubscription();
    /**
     * Event used to handle errors. WidgetSubject enables tracking and reacting to errors during the widget's lifecycle.
     */
    public onError = new WidgetSubject<WidgetError>();
    private gameIdent: string;

    /**
     * Constructor initializes the widget and adds it to the list of managed widgets.
     * @param widget - The widget to be managed.
     */
    constructor(private widget: T) {
        this.gameIdent = widget.config.game.ident;
        this.subscription.add(this.onError);
        this.getInitializedWidgets(this.gameIdent).push(widget);
    }

    /**
     * Destroys the widget and removes its instance from the managed widgets.
     */
    public destroy() {
        this.subscription.unsubscribe();
        this.widget.destroy();
        PublicModuleApi.initializedWidgets[this.gameIdent] = this.getInitializedWidgets(this.gameIdent).filter(w => w.elementId != this.widget.elementId);
        PublicModuleApi.destroyState(this.gameIdent);
    }

    /**
     * Destroys all managed widgets.
     */
    public destroyAll() {
        PublicModuleApi.destroyAll(this.gameIdent);
    }

    /**
    * Destroys all managed widgets.
    */
    public static destroyAll(gameIdent: string) {
        if(PublicModuleApi.initializedWidgets[gameIdent]){
            PublicModuleApi.initializedWidgets[gameIdent].forEach(w => {
                w.destroy();
            });
            PublicModuleApi.initializedWidgets[gameIdent] = [];
            PublicModuleApi.destroyState(gameIdent);
        }
    }

    private static destroyState(gameIdent: string) {
        if (PublicModuleApi.initializedWidgets[gameIdent] && PublicModuleApi.initializedWidgets[gameIdent].length == 0)
            AppState.getInstance(gameIdent).destroy();
    }

    private getInitializedWidgets(gameIdent: string){
        if(!PublicModuleApi.initializedWidgets[gameIdent])
            PublicModuleApi.initializedWidgets[gameIdent] = [];
        return PublicModuleApi.initializedWidgets[gameIdent];
    }
}